import { useContext } from 'react'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import * as dataStoreItemActions from '@redux/modules/dataStoreItem'

import defaultFormState, { requiredFields } from '@models/dataStoreItem'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  DataStoreItemModel, DataStoreItemRequestOptions, DigitalTemplateModel, UserModel,
} from '@models/types'

const stringifyObjectKeys: (keyof DataStoreItemModel)[] = ['data']

type CreateOrUpdateDataStoreItemParams = {
  behaviour: 'add' | 'group' | 'replace',
  dataStoreItemParams: Partial<DataStoreItemModel>,
  dispatch: AppDispatch,
  requestOptions?: DataStoreItemRequestOptions,
}

const createOrUpdateDataStoreItem = (params: CreateOrUpdateDataStoreItemParams) => {
  const {
    behaviour, dispatch, dataStoreItemParams, requestOptions,
  } = params
  const { createOrUpdateDataStoreItem: createOrUpdateFn } = dataStoreItemActions

  const { data, value } = dataStoreItemParams

  const updatedParams = {
    ...dataStoreItemParams,
    data: JSON.stringify(data),
    value: value === null || typeof value === 'undefined' ? '' : `${value}`,
  }

  // @ts-expect-error TS2345
  return dispatch(createOrUpdateFn(updatedParams, behaviour, requestOptions))
}

type UpdateDataStoreItemParams = {
  dataStoreItem: DataStoreItemModel,
  dataStoreItemParams: Partial<DataStoreItemModel>,
  dispatch: AppDispatch,
  requestOptions?: DataStoreItemRequestOptions,
}

const updateDataStoreItem = (params: UpdateDataStoreItemParams) => {
  const {
    dispatch, dataStoreItem, dataStoreItemParams, requestOptions,
  } = params
  const { updateDataStoreItem: updateFn } = dataStoreItemActions

  const updatedParams = mergeAndStringifyEntityObjects(dataStoreItem, dataStoreItemParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteDataStoreItemParams = {
  dataStoreItem: DeleteParams<DataStoreItemModel>,
  dispatch: AppDispatch,
}

const deleteDataStoreItem = (params: DeleteDataStoreItemParams) => {
  const { dispatch, dataStoreItem } = params
  const { deleteDataStoreItem: deleteFn } = dataStoreItemActions

  return dispatch(deleteFn(dataStoreItem))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useDataStoreItemForm(
  dataStoreItem: Partial<DataStoreItemModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const dataStoreItemForm = useForm(
    defaultFormState,
    { entity: dataStoreItem, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [dataStoreItem.id, dataStoreItem.cache_key],
  )

  return {
    ...dataStoreItemForm,
  }
}

export type UseDataStoreItemFormPayload = ReturnType<typeof useDataStoreItemForm>

export function useRelations(dataStoreItem: Partial<DataStoreItemModel>) {
  const { digital_template_id, editor_id, editor_type } = dataStoreItem || {}

  const { digitalTemplates, users } = useSelector(reduxState => reduxState.entities)

  const digitalTemplate: Partial<DigitalTemplateModel> = digital_template_id
    ? digitalTemplates[digital_template_id] || {} : {}
  // @ts-expect-error TS2538
  const user: Partial<UserModel> = editor_type === 'User' ? users[editor_id] || {} : {}

  return { digitalTemplate, user }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: DataStoreItemRequestOptions,
}

function useDataStoreItem(initEntity: Partial<DataStoreItemModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: dataStoreItem }: { entity: DataStoreItemModel} = useLatestEntity(initEntity, 'dataStoreItems')
  const { id } = dataStoreItem

  const dispatch = useDispatch()

  useReduxAction(
    'dataStoreItems',
    'loadDataStoreItem',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { callbacks } = useContext(PageContext)

  const {
    deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.dataStoreItems)

  const { digitalTemplate, user } = useRelations(dataStoreItem)

  const secured = digObject(dataStoreItem, 'data.secured', false)

  return {
    callbacks: {
      editDataStoreItem: () => launchModal({
        callbacks,
        modalKey: 'EditDataStoreItemModal',
        payload: { dataStoreItem },
      }),
      deleteDataStoreItem: () => deleteDataStoreItem({ dispatch, dataStoreItem }),
      createOrUpdateDataStoreItem: (
        behaviour: 'add' | 'group' | 'replace',
        dataStoreItemParams: Partial<DataStoreItemModel>,
        entityOptions?: DataStoreItemRequestOptions,
      ) => (
        createOrUpdateDataStoreItem({
          behaviour, dataStoreItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateDataStoreItem: (
        dataStoreItemParams: Partial<DataStoreItemModel>,
        entityOptions?: DataStoreItemRequestOptions,
      ) => (
        updateDataStoreItem({
          dataStoreItem, dataStoreItemParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    dataStoreItem,
    deleting,
    digitalTemplate,
    loading: loading && !loadedIds.includes(dataStoreItem.id),
    secured,
    updating,
    user,
  }
}

export default useDataStoreItem
